import { useEffect, useId, useRef, useState } from "react";
import { ghlReadingGap, type GhlImporterStatus, type GhlImportReport, type GhlImportRun } from "@gioia365/contracts";
import { useTab } from "../api/queries";
import { Button } from "../design/components/Button";
import { Checkbox } from "../design/components/Checkbox";
import { DataTable } from "../design/components/DataTable";
import { Reason } from "../design/components/Reason";
import { useToast } from "../design/components/Toast";
import { messageForError } from "../i18n/errors";
import { formatCount } from "../i18n/format";
import { useLocale, useT } from "../i18n/locale";
import { useAccess } from "../session/access";
import { whenParts } from "./close-import-format";
import { useGhlReadActions } from "./ghl-read-queries";

// L'importazione da GoHighLevel nella pagina «GoHighLevel» (INGR-30, decisioni di Lucio del 24/10/2026), soltanto per il Super admin di
// Gioia365: 1. «Prova senza salvare» e il suo rapporto (soltanto numeri e nomi dei tag, INGR-17); 2. i tag voce per voce, con «Conferma i
// tag» (INGR-2: niente si importa senza); 3. «Importa adesso», con una conferma, e il rapporto di ciò che ha scritto. Mentre una prova o
// un'importazione gira lo stato si rilegge da solo (`ghlReadQuery`). Owner, tag e Smart List dei Lead nuovi sono quelli del profilo del
// server (`ghl-import/profile.ts`).


/** Un tag «Owner: Nome Cognome» non entra mai su un Lead (INGR-8): il server lo rifiuta, qui non si spunta. */
const isOwnerTag = (name: string) => /^owner\s*:/i.test(name.trim());

type Props = { importer: GhlImporterStatus; connected: boolean };

export function GhlImportPart({ importer, connected }: Props) {
  const t = useT();
  const titleId = useId();
  return (
    <section className="g-ci__nightly-part" aria-labelledby={titleId}>
      <h3 id={titleId}>{t("ghlImport.title")}</h3>
      <p className="g-ci__muted">{t("ghlImport.intro", { owner: importer.newLeads.owner, tag: importer.newLeads.tag, list: importer.newLeads.smartList })}</p>
      {importer.closeBusy ? <Reason tone="warning">{t("ghlImport.closeBusy")}</Reason> : null}
      {connected ? (
        <>
          <DryRunPart importer={importer} />
          {importer.dryRun?.status === "completed" && importer.dryRunCurrent && importer.dryRun.report ? <TagsPart key={`${importer.dryRun.runId}|${importer.tagChoices?.at ?? ""}`} importer={importer} run={importer.dryRun} report={importer.dryRun.report} /> : null}
          <ImportPart importer={importer} />
        </>
      ) : (
        <p>{t("ghlImport.needsKey")}</p>
      )}
    </section>
  );
}

function RunLine({ run }: { run: GhlImportRun }) {
  const t = useT();
  const access = useAccess();
  const timeZone = access.context.settings.timeZone;
  const started = whenParts(timeZone, run.startedAt);
  if (run.status === "running") return <p className="g-ci__muted">{t("ghlImport.running", started)}</p>;
  const finished = run.finishedAt ? whenParts(timeZone, run.finishedAt) : null;
  return (
    <>
      <p className="g-ci__muted">
        {t("ghlImport.started", started)} {finished ? t("ghlImport.finished", finished) : null}
      </p>
      {run.status !== "completed" ? <Reason tone="warning">{t("ghlImport.failed", { code: run.error ?? run.status })}</Reason> : null}
    </>
  );
}

function DryRunPart({ importer }: { importer: GhlImporterStatus }) {
  const t = useT();
  const tab = useTab();
  const toast = useToast();
  const actions = useGhlReadActions(tab);
  const start = () =>
    actions.dryRun.mutate(undefined, {
      onSuccess: () => toast.show(t("ghlImport.dryRunToast"), "success"),
      onError: (error) => toast.show(messageForError(t, error), "warning"),
    });
  const run = importer.dryRun;
  return (
    <div className="g-ci__nightly-part">
      <h4>{t("ghlImport.dryRunTitle")}</h4>
      <p className="g-ci__muted">{t("ghlImport.dryRunText")}</p>
      {run ? <RunLine run={run} /> : <p>{t("ghlImport.noRun")}</p>}
      {run && !importer.dryRunCurrent ? <Reason tone="warning">{t("ghlImport.dryRunOld")}</Reason> : null}
      {run?.report ? <Report report={run.report} smartList={importer.newLeads.smartList} /> : null}
      <div className="g-ci__nightly-actions">
        <Button onClick={start} disabled={importer.busy || importer.closeBusy || actions.dryRun.isPending}>
          {t(actions.dryRun.isPending ? "ghlImport.dryRunStarting" : "ghlImport.dryRunStart")}
        </Button>
      </div>
    </div>
  );
}

/** Il rapporto di una prova o di un'importazione: soltanto numeri. */
function Report({ report, smartList }: { report: GhlImportReport; smartList: string }) {
  const t = useT();
  const { locale } = useLocale();
  const n = (value: number) => formatCount(locale, value);
  const c = report.contacts;
  const gap = ghlReadingGap(report);
  return (
    <div className="g-ci__report">
      <h5>{t("ghlImport.contactsTitle")}</h5>
      <ul className="g-ci__ghl-counts">
        <li>{t("ghlImport.read", { count: n(c.read) })}</li>
        <li>{t("ghlImport.matchedByContact", { count: n(c.matched.byContact) })}</li>
        <li>{t("ghlImport.matchedByEmail", { count: n(c.matched.byEmail) })}</li>
        <li>{t("ghlImport.matchedByPhone", { count: n(c.matched.byPhone) })}</li>
        <li>{t("ghlImport.matchedArchived", { count: n(c.matchedArchived) })}</li>
        <li>{t(report.written ? "ghlImport.created" : "ghlImport.toCreate", { count: n(c.toCreate) })}</li>
        {c.sameNewLead > 0 ? <li>{t("ghlImport.sameNewLead", { count: n(c.sameNewLead) })}</li> : null}
        <li>{t("ghlImport.candidates", { count: n(c.candidates.total), already: n(c.candidates.alreadyLeads) })}</li>
        <li>{t("ghlImport.uncertain", { count: n(c.uncertain.total), matched: n(c.uncertain.matched), notCreated: n(c.uncertain.notCreated) })}</li>
      </ul>
      {!report.owner.found ? <Reason tone="warning">{t("ghlImport.ownerMissing", { owner: report.owner.name })}</Reason> : null}
      <h5>{t("ghlImport.discardedTitle")}</h5>
      <ul className="g-ci__ghl-counts">
        <li>{t("ghlImport.noItalianMobile", { count: n(c.discarded.noItalianMobile) })}</li>
        <li>{t("ghlImport.notQualified", { count: n(c.discarded.notQualified) })}</li>
        <li>{t("ghlImport.notHairdresser", { count: n(c.discarded.notHairdresser) })}</li>
      </ul>
      <p className="g-ci__muted">
        {report.hiringPipeline.readable ? t("ghlImport.pipelineRead", { pipelines: n(report.hiringPipeline.pipelines), count: n(report.hiringPipeline.contacts) }) : t("ghlImport.pipelineUnreadable")}
      </p>
      <h5>{t("ghlImport.conversationsTitle")}</h5>
      <ul className="g-ci__ghl-counts">
        <li>{t("ghlImport.conversations", { count: n(report.conversations.read), outOfScope: n(report.conversations.outOfScope) })}</li>
        {report.messages.map((line) => (
          <li key={line.channel}>{t("ghlImport.channelLine", { channel: t(`ghlImport.channels.${line.channel}`), single: n(line.single), counted: n(line.counted) })}</li>
        ))}
      </ul>
      {report.skippedMessages.length > 0 ? (
        <>
          <h5>{t("ghlImport.skippedTitle")}</h5>
          <ul className="g-ci__ghl-counts">
            {report.skippedMessages.map((line) => (
              <li key={line.kind}>{t("ghlImport.skippedLine", { kind: line.kind, count: n(line.count) })}</li>
            ))}
          </ul>
        </>
      ) : null}
      {report.written ? (
        <>
          <h5>{t("ghlImport.writtenTitle")}</h5>
          <ul className="g-ci__ghl-counts">
            <li>{t("ghlImport.leadsCreated", { count: n(report.written.leadsCreated), already: n(report.written.leadsAlreadyCreated) })}</li>
            <li>{t("ghlImport.tagsAdded", { count: n(report.written.tagsAdded) })}</li>
            <li>{t("ghlImport.messagesWritten", { count: n(report.written.messages) })}</li>
            <li>{t("ghlImport.summariesWritten", { count: n(report.written.summaries) })}</li>
            <li>{t("ghlImport.alreadyImported", { count: n(report.written.alreadyImported) })}</li>
            <li>{t(`ghlImport.smartList.${report.written.smartList}`, { list: smartList })}</li>
          </ul>
        </>
      ) : null}
      {gap.contacts > 0 || gap.conversations > 0 ? (
        <Reason tone={gap.complete ? "info" : "warning"}>
          {t(gap.complete ? "ghlImport.gapTolerated" : "ghlImport.incomplete", {
            contacts: n(report.listed.contacts ?? c.read),
            read: n(c.read),
            conversations: n(report.listed.conversations ?? report.conversations.read + report.conversations.outOfScope),
            readConversations: n(report.conversations.read + report.conversations.outOfScope),
          })}
        </Reason>
      ) : null}
      <p className="g-ci__muted">{t("ghlImport.requests", { count: n(report.requests) })}</p>
    </div>
  );
}

function TagsPart({ importer, run, report }: { importer: GhlImporterStatus; run: GhlImportRun; report: GhlImportReport }) {
  const t = useT();
  const tab = useTab();
  const toast = useToast();
  const { locale } = useLocale();
  const access = useAccess();
  const actions = useGhlReadActions(tab);
  const baseId = useId();
  const confirmed = importer.tagChoices;
  // Le spunte partono dai tag confermati, se no dai destini di partenza; una prova nuova o una conferma rimontano la parte (`key`).
  const [entering, setEntering] = useState<Set<string>>(() => new Set(confirmed ? confirmed.entering : report.tags.filter((tag) => tag.destiny === "enter").map((tag) => tag.name)));
  const toggle = (name: string, on: boolean) =>
    setEntering((current) => {
      const next = new Set(current);
      if (on) next.add(name);
      else next.delete(name);
      return next;
    });
  const save = () =>
    actions.saveTags.mutate(
      { runId: run.runId, entering: [...entering] },
      {
        onSuccess: () => toast.show(t("ghlImport.tagsToast"), "success"),
        onError: (error) => toast.show(messageForError(t, error), "warning"),
      },
    );
  const when = confirmed ? whenParts(access.context.settings.timeZone, confirmed.at) : null;
  return (
    <div className="g-ci__nightly-part">
      <h4>{t("ghlImport.tagsTitle")}</h4>
      <p className="g-ci__muted">{t("ghlImport.tagsText")}</p>
      {confirmed && when ? (
        <p>
          {confirmed.by
            ? t("ghlImport.tagsConfirmed", { ...when, person: confirmed.by, count: formatCount(locale, confirmed.entering.length) })
            : t("ghlImport.tagsConfirmedBySomeone", { ...when, count: formatCount(locale, confirmed.entering.length) })}
        </p>
      ) : null}
      {report.tags.length === 0 ? (
        <p>{t("ghlImport.tagsNone")}</p>
      ) : (
        <div className="g-ci__table g-ci__table--vars">
          <DataTable caption={t("ghlImport.tagsTitle")} captionHidden>
            <thead>
              <tr>
                <th scope="col">{t("ghlImport.enterColumn")}</th>
                <th scope="col" className="g-ci__num">
                  {t("ghlImport.contactsColumn")}
                </th>
                <th scope="col">{t("ghlImport.destinyColumn")}</th>
              </tr>
            </thead>
            <tbody>
              {report.tags.map((tag, index) => (
                <tr key={tag.name}>
                  <td>
                    <Checkbox
                      id={`${baseId}-${index}`}
                      label={tag.name}
                      checked={tag.destiny !== "candidates" && !isOwnerTag(tag.name) && entering.has(tag.name)}
                      disabled={tag.destiny === "candidates" || isOwnerTag(tag.name) || importer.busy}
                      onChange={(on) => toggle(tag.name, on)}
                    />
                  </td>
                  <td className="g-ci__num">{formatCount(locale, tag.contacts)}</td>
                  <td>{t(`ghlImport.destiny.${tag.destiny}`)}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </div>
      )}
      <div className="g-ci__nightly-actions">
        <Button variant="primary" onClick={save} disabled={importer.busy || actions.saveTags.isPending}>
          {t(actions.saveTags.isPending ? "ghlImport.tagsConfirming" : "ghlImport.tagsConfirm")}
        </Button>
      </div>
    </div>
  );
}

function ImportPart({ importer }: { importer: GhlImporterStatus }) {
  const t = useT();
  const tab = useTab();
  const toast = useToast();
  const actions = useGhlReadActions(tab);
  const [confirming, setConfirming] = useState(false);
  const yes = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (confirming) yes.current?.focus();
  }, [confirming]);
  const start = () =>
    actions.importNow.mutate(undefined, {
      onSuccess: () => {
        setConfirming(false);
        toast.show(t("ghlImport.importToast"), "success");
      },
      onError: (error) => toast.show(messageForError(t, error), "warning"),
    });
  const run = importer.importRun;
  const canStart = importer.ready && !importer.busy && !importer.closeBusy;
  return (
    <div className="g-ci__nightly-part">
      <h4>{t("ghlImport.importTitle")}</h4>
      <p className="g-ci__muted">{t("ghlImport.importText")}</p>
      {!importer.ready ? <p>{t("ghlImport.importNeeds")}</p> : null}
      {run ? <RunLine run={run} /> : null}
      {run?.report ? <Report report={run.report} smartList={importer.newLeads.smartList} /> : null}
      {confirming ? (
        <div className="g-ci__nightly-confirm" role="group" aria-label={t("ghlImport.importStart")}>
          <p>{t("ghlImport.importConfirm")}</p>
          <div className="g-ci__nightly-actions">
            <Button ref={yes} variant="primary" onClick={start} disabled={!canStart || actions.importNow.isPending}>
              {t("ghlImport.importYes")}
            </Button>
            <Button onClick={() => setConfirming(false)}>{t("common.cancel")}</Button>
          </div>
        </div>
      ) : (
        <div className="g-ci__nightly-actions">
          <Button onClick={() => setConfirming(true)} disabled={!canStart}>
            {t("ghlImport.importStart")}
          </Button>
        </div>
      )}
    </div>
  );
}
